import { Visibility, VisibilityOff } from '@mui/icons-material'
import {
  Box,
  Button,
  DialogActions,
  IconButton,
  InputAdornment,
  Modal,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import type React from 'react'
import { useContext, useEffect, useRef, useState } from 'react'
import { loginIdSchema, strictPasswordSchema } from '../schema/loginSchema'
import { rhqSchema } from '../schema/organizationSchema'
import { LanguageContext } from '../util/LanguageContext'

type ModalRHQUserAddDialogProps = {
  open: boolean
  title: string
  onClose: () => void
  onSave: (args: {
    loginId: string
    password: string
    rhq?: string
    salesCompany?: string
    role: number
  }) => void
}

export const ModalRHQUserAddDialog: React.FC<ModalRHQUserAddDialogProps> = props => {
  const { open, onClose, onSave } = props
  const [isOpen, setIsOpen] = useState(open)
  const [rhq, setRhq] = useState('')
  const [loginId, setLoginId] = useState('')
  const [password, setPassword] = useState('')
  const [isButtonDisabled, setIsButtonDisabled] = useState(true)
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [showPassword, setShowPassword] = useState(false)
  const [errorRhq, setErrorRhq] = useState('')
  const [errorLoginId, setErrorLoginId] = useState('')
  const [errorPassword, setErrorPassword] = useState('')
  // 文字列変換用
  const translation = useContext(LanguageContext)

  const validateRhq = async (value: string) => {
    try {
      await rhqSchema.validate(value)
      setErrorRhq('')
      if (rhq !== '' && loginId !== '' && password !== '' && errorPassword === '')
        setIsButtonDisabled(false)
    } catch (err: any) {
      setErrorRhq(err.message)
      setIsButtonDisabled(true)
    }
  }

  const validateLoginId = async (value: string) => {
    try {
      await loginIdSchema.validate(value)
      setErrorLoginId('')
      if (rhq !== '' && loginId !== '' && password !== '' && errorPassword === '')
        setIsButtonDisabled(false)
    } catch (err: any) {
      setErrorLoginId(err.message)
      setIsButtonDisabled(true)
    }
  }

  const validatePassword = async (value: string) => {
    try {
      await strictPasswordSchema.validate(value)
      setErrorPassword('')
      if (rhq !== '' && loginId !== '' && password !== '' && errorPassword === '')
        setIsButtonDisabled(false)
    } catch (err: any) {
      setErrorPassword(err.message)
      setIsButtonDisabled(true)
    }
  }

  const handleRhqChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRhq(event.target.value)
    validateRhq(event.target.value)
  }

  const handleLoginidChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLoginId(event.target.value)
    validateLoginId(event.target.value)
    // setIsButtonDisabled(event.target.value === "" || password === "");
  }

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value)
    validatePassword(event.target.value)
    // setIsButtonDisabled(event.target.value === "" || loginId === "");
  }

  const handleTogglePasswordVisibility = () => {
    setShowPassword(prev => !prev)
  }

  const handleSave = () => {
    // roleは管理者 - 2:RHQ Administrator に固定
    onSave({ rhq, loginId, password, role: 2 })
    onClose()
    setIsOpen(false)
  }
  const handleClose = (event: React.MouseEvent<HTMLInputElement>, reason: 'backdropClick') => {
    if (reason === 'backdropClick') return
    onClose()
    setIsOpen(false)
  }
  useEffect(() => {
    setIsButtonDisabled(rhq === '' || loginId === '' || password === '')
  }, [rhq, loginId, password])

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }, [])

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '40%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '787px',
          bgcolor: 'background.paper',
          borderRadius: '10px',
          boxShadow: '0px 2px 2px #00000014',
        }}
      >
        <Stack direction="column" justifyContent="center" alignItems="center">
          {/* ダイアログタイトル: 新規追加 */}
          <Typography
            sx={{
              mt: '48px',
              font: 'normal normal bold 32px/48px BIZ UDGothic',
              textAlign: 'center',
            }}
          >
            {translation['button_add_new']}
          </Typography>

          {/* RHQ */}
          <Typography
            sx={{
              left: '190px',
              width: '402px',
              mt: '50px',
              color: '#282828',
              font: 'normal normal normal 21px/32px Noto Sans JP',
              textAlign: 'left',
            }}
          >
            {translation['label_RHQ_info']}
          </Typography>
          <TextField
            value={rhq}
            error={!!errorRhq}
            helperText={errorRhq ? translation[errorRhq] : ' '}
            onChange={handleRhqChange}
            InputProps={{ sx: { background: '#F4F4F4' } }}
            sx={{
              width: '402px',
              borderRadius: '10px',
              '& .MuiFormHelperText-root': {
                whiteSpace: 'nowrap',
              },
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: errorRhq ? 'red' : '#282828',
                },
              },
            }}
          />

          {/* ログインID */}
          <Typography
            sx={{
              left: '190px',
              width: '402px',
              color: '#282828',
              font: 'normal normal normal 21px/32px Noto Sans JP',
              textAlign: 'left',
            }}
          >
            {translation['label_login_id']}
          </Typography>
          <TextField
            value={loginId}
            error={!!errorLoginId}
            helperText={errorLoginId ? translation[errorLoginId] : ' '}
            onChange={handleLoginidChange}
            InputProps={{ sx: { background: '#F4F4F4' } }}
            sx={{
              width: '402px',
              borderRadius: '10px',
              '& .MuiFormHelperText-root': {
                whiteSpace: 'nowrap',
              },
              '& .MuiOutlinedInput-root': {
                // "& fieldset": {
                //   borderColor: "white",
                // },
                // "&:hover fieldset": {
                //   borderColor: "white",
                // },
                '&.Mui-focused fieldset': {
                  borderColor: errorLoginId ? 'red' : '#282828',
                },
              },
            }}
          />

          {/* パスワード */}
          <Typography
            sx={{
              left: '190px',
              width: '402px',
              color: '#282828',
              font: 'normal normal normal 21px/32px Noto Sans JP',
              textAlign: 'left',
            }}
          >
            {translation['label_password']}
          </Typography>
          <TextField
            type={showPassword ? 'text' : 'password'}
            value={password}
            error={!!errorPassword}
            helperText={errorPassword ? translation[errorPassword] : ' '}
            onChange={handlePasswordChange}
            FormHelperTextProps={{
              style: { whiteSpace: 'normal' }, // ここで改行を許可します
            }}
            InputProps={{
              endAdornment: password ? (
                <InputAdornment position="end">
                  <IconButton onClick={handleTogglePasswordVisibility}>
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ) : null,
              sx: {
                background: '#F4F4F4',
              },
            }}
            sx={{
              width: '402px',
              borderRadius: '10px',
              height: '100px',
              'input::-ms-reveal': {
                display: 'none' /* -ms-revealアイコンを非表示にする */,
              },
              '& .MuiFormHelperText-root': {
                whiteSpace: 'nowrap',
              },
              '& .MuiOutlinedInput-root': {
                // "& fieldset": {
                //   borderColor: "white",
                // },
                // "&:hover fieldset": {
                //   borderColor: "white",
                // },
                '&.Mui-focused fieldset': {
                  borderColor: errorPassword ? 'red' : '#282828',
                },
              },
            }}
            // variant="outlined"
          />
          <DialogActions sx={{ mb: '60px' }}>
            <Button
              onClick={onClose}
              sx={{
                height: '49px',
                width: '181px',
                font: 'normal normal bold 21px/29px Noto Sans JP',
                color: '#282828',
                background: '#FFFFFF 0% 0% no-repeat padding-box',
                border: '2px solid #282828',
                borderRadius: '10px',
                mt: '5px',
                mr: '30px',
                '&:hover': {
                  background: '#FFFFFF',
                  opacity: 0.8,
                },
              }}
            >
              {translation['button_cancel']}
            </Button>
            <Button
              type="submit"
              disabled={isButtonDisabled}
              onClick={handleSave}
              sx={{
                height: '49px',
                width: '181px',
                font: 'normal normal bold 21px/29px Noto Sans JP',
                color: isButtonDisabled ? '#CACACA' : '#FFFFFF',
                background: isButtonDisabled ? '#F4F4F4 0% 0% no-repeat padding-box' : '#282828',
                borderRadius: '10px',
                mt: '5px',
                '&:hover': {
                  backgroundColor: '#282828',
                  opacity: 0.8,
                  boxShadow: 0,
                },
              }}
            >
              {translation['button_add']}
            </Button>
          </DialogActions>
        </Stack>
      </Box>
    </Modal>
  )
}
